import React, { useEffect } from 'react';
import { type useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import type { TableProps } from './Table';
import PerfectScrollbar from 'react-perfect-scrollbar';
import InputFieldFactory from '../Factory/InputFieldFactory';
import type { Field } from '../../../types';
import { Card, IconButton, Box, Button } from '@mui/material';
import {
  PaperStyled,
  TableBodyStyled,
  TableCellStyled,
  TableHeadStyled,
  TableRowStyled,
  TableStyled,
  DeleteIcon,
  AddIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
} from './Table.styles';
import useInternalTable from './useInternalTable';
import type { TableColumn, RowData } from './useInternalTable';

type InternalTableProps = TableProps & {
  formik: ReturnType<typeof useFormikContext>;
};

const InternalTable = ({ field, formik, isEditing = true }: InternalTableProps) => {
  const { label, name, value, defaultValue, ...rest } = field || {};

  // should be ignored!
  delete rest.schema;

  const columns = React.useMemo(() => {
    return field?.options?.columns ?? [];
  }, [field]);

  const intl = useIntl();

  const { initialValues, setFieldValue, isSubmitting } = formik;

  const { localRows, isCollapsed, handleFieldChange, addEmptyRow, handleDeleteRow, toggleCollapse, syncToFormik } =
    useInternalTable({
      name,
      columns,
      initialValues,
      setFieldValue,
    });

  // Sync with Formik on form submission
  useEffect(() => {
    if (isSubmitting) {
      syncToFormik(localRows);
    }
  }, [isSubmitting, syncToFormik, localRows]);

  // Render editable cell for any row
  const renderEditableCell = (column: TableColumn, rowIndex: number, rowData: RowData) => {
    const fieldConfig: Field = {
      type: column.fieldType || 'text',
      name: `${name}.${rowIndex}.${column.field}`,
      label: intl.formatMessage({ id: column.header }),
      value: rowData[column.field] ?? '',
      disabled: false,
      options: column.options || {},
    };

    return (
      <InputFieldFactory
        key={fieldConfig.name}
        field={fieldConfig}
        onChange={({ value }: { value: any }) => handleFieldChange(rowIndex, column.field, value)}
        hideErrorText={true}
      />
    );
  };

  return (
    <PaperStyled>
      <Card>
        <PerfectScrollbar>
          <TableStyled size="small" compact>
            <TableHeadStyled>
              <TableRowStyled>
                {columns.map(column => {
                  return (
                    <TableCellStyled size="small" key={column.header}>
                      {intl.formatMessage({ id: column.header })}
                    </TableCellStyled>
                  );
                })}
                <TableCellStyled sx={{ width: '60px', minWidth: '60px' }}>
                  <IconButton size="small" onClick={toggleCollapse}>
                    {localRows.length > 0 && (
                      <>{isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}</>
                    )}
                  </IconButton>
                </TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            {!isCollapsed && (
              <TableBodyStyled>
                {localRows.length > 0
                  ? localRows.map((rowData, index) => (
                      <TableRowStyled key={index} hover={true}>
                        {columns.map((column, colIndex) => (
                          <TableCellStyled
                            key={`${index}-${column.header}`}
                            colSpan={!isEditing && colIndex === columns.length - 1 ? 2 : 1}
                          >
                            {isEditing ? renderEditableCell(column, index, rowData) : rowData[column.field] ?? ''}
                          </TableCellStyled>
                        ))}
                        {isEditing && (
                          <TableCellStyled>
                            <IconButton size="small" onClick={() => handleDeleteRow(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCellStyled>
                        )}
                      </TableRowStyled>
                    ))
                  : null}
              </TableBodyStyled>
            )}
          </TableStyled>
        </PerfectScrollbar>

        {isEditing && !isCollapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 1 }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addEmptyRow}>
              {intl.formatMessage({ id: 'button.addRow', defaultMessage: 'Add row' })}
            </Button>
          </Box>
        )}
      </Card>
    </PaperStyled>
  );
};

export default InternalTable;
